/**
 * Centralized Role-Based Access Control (RBAC) Configuration
 *
 * This file serves as the SINGLE SOURCE OF TRUTH for all permissions in the application.
 *
 * User Type Classification:
 * 1. SuperAdmin - Global administrator, no program selection
 * 2. Admin - Facility administrator, no program selection
 * 3. TierUser - Government officials, paired with user_location
 * 4. FacilityUser - Lab staff (Supervisor, Analyst, etc.), MUST select program
 */

import { UserRoleEnums } from "../Constant";
import { programEnum } from "../Helpers";

// ============================================================
// USER TYPE DEFINITIONS
// ============================================================

export const USER_TYPES = {
  SUPER_ADMIN: "SuperAdmin",
  ADMIN: "Admin",
  TIER_USER: "TierUser",
  FACILITY_USER: "FacilityUser",
};

/**
 * Roles that DON'T require program selection
 */
export const PROGRAM_INDEPENDENT_ROLES = [
  UserRoleEnums.SUPER_ADMIN,
  UserRoleEnums.ADMIN,
  UserRoleEnums.TIER_USER,
];

/**
 * Facility user roles that MUST select a program
 */
export const FACILITY_ROLES = [
  UserRoleEnums.SUPERVISOR,
  UserRoleEnums.ANALYST,
  UserRoleEnums.DATA_CLERK,
  UserRoleEnums.RECEPTIONIST,
  UserRoleEnums.DATA_ANALYST,
  UserRoleEnums.MANAGER,
];

// ============================================================
// DEFAULT ROUTES
// ============================================================

/**
 * Default landing pages for each user type/role
 */
export const DEFAULT_ROUTES = {
  // Global Admin
  [UserRoleEnums.SUPER_ADMIN]: "/administrations/global/dashboard",

  // Facility Admin
  [UserRoleEnums.ADMIN]: "/administrations/facility/facilityUsers",

  // Tier Users (by location level)
  [UserRoleEnums.TIER_USER]: {
    // 1: "/dashboards/federal", // Federal level
    // 2: "/dashboards/state", // State level
    // 3: "/dashboards/lga", // LGA level
    default: "/dashboards/public/priority_diseases",
  },

  // Facility Users (by program and role)
  FACILITY_USERS: {
    // GENERAL Program
    [programEnum.GENERAL]: {
      [UserRoleEnums.SUPERVISOR]: "/clinical/manage-sample",
      [UserRoleEnums.ANALYST]: "/clinical/workplan",
      [UserRoleEnums.DATA_CLERK]: "/clinical/lab-order",
      [UserRoleEnums.RECEPTIONIST]: "/clinical/lab-order",
      [UserRoleEnums.DATA_ANALYST]: "/clinical/dashboard",
      [UserRoleEnums.MANAGER]: "/clinical/dashboard",
    },

    // TB Program
    [programEnum.TB]: {
      [UserRoleEnums.SUPERVISOR]: "/programs/tb-manage-sample",
      [UserRoleEnums.ANALYST]: "/programs/tb-workplan",
      [UserRoleEnums.DATA_CLERK]: "/programs/tb-manage-sample",
      [UserRoleEnums.RECEPTIONIST]: "/programs/tb-manage-sample",
      [UserRoleEnums.DATA_ANALYST]: "/programs/tb-dashboard",
    },

    // EID Program
    [programEnum.EID]: {
      [UserRoleEnums.SUPERVISOR]: "/programs/eid-manage-sample",
      [UserRoleEnums.ANALYST]: "/programs/eid-workplan",
      [UserRoleEnums.DATA_CLERK]: "/programs/eid-manage-sample",
      [UserRoleEnums.RECEPTIONIST]: "/programs/eid-manage-sample",
      [UserRoleEnums.DATA_ANALYST]: "/dashboards/eid",
    },

    // Viral Load Program
    [programEnum.VIRAL_LOAD]: {
      [UserRoleEnums.SUPERVISOR]: "/programs/vl-manage-sample",
      [UserRoleEnums.ANALYST]: "/programs/vl-workplan",
      [UserRoleEnums.DATA_CLERK]: "/programs/vl-manage-sample",
      [UserRoleEnums.RECEPTIONIST]: "/programs/vl-manage-sample",
      [UserRoleEnums.DATA_ANALYST]: "/dashboards/viral_load",
    },

    // ESPN Program
    [programEnum.ESPN]: {
      [UserRoleEnums.SUPERVISOR]: "/espn/dashboard",
      [UserRoleEnums.ANALYST]: "/espn/dashboard",
      [UserRoleEnums.DATA_CLERK]: "/espn/dashboard",
      [UserRoleEnums.RECEPTIONIST]: "/espn/dashboard",
      [UserRoleEnums.DATA_ANALYST]: "/espn/dashboard",
    },

    // Public Health Programs (COVID, Lassa, Malaria, etc.)
    PUBLIC_HEALTH: {
      [UserRoleEnums.SUPERVISOR]: "/programs/ph-manage-sample",
      [UserRoleEnums.ANALYST]: "/programs/ph-workplan",
      [UserRoleEnums.DATA_CLERK]: "/programs/ph-manage-sample",
      [UserRoleEnums.RECEPTIONIST]: "/programs/ph-manage-sample",
      [UserRoleEnums.DATA_ANALYST]: "/programs/ph-dashboard",
    },
  },
};

// ============================================================
// ROUTE PERMISSIONS
// ============================================================

/**
 * Maps routes to allowed roles
 * Format: { route: [allowedRoles], ... }
 */
export const ROUTE_PERMISSIONS = {
  "/dashboards/public/monitoring": ["*"],

  // Global Administration (SuperAdmin only)
  "/administrations/global/dashboard": [UserRoleEnums.SUPER_ADMIN],
  "/administrations/global/manageFacility": [UserRoleEnums.SUPER_ADMIN],
  "/administrations/global/tierUsers": [UserRoleEnums.SUPER_ADMIN],
  "/administrations/global/testResultConfiguration": [
    UserRoleEnums.SUPER_ADMIN,
  ],
  "/administrations/global/support-requests": [UserRoleEnums.SUPER_ADMIN],

  // Facility Administration (Facility Admin)
  "/administrations/facility/facilityUsers": [
    UserRoleEnums.ADMIN,
    // UserRoleEnums.SUPER_ADMIN,
  ],
  "/administrations/facility/testService": [
    UserRoleEnums.ADMIN,
    // UserRoleEnums.SUPER_ADMIN,
  ],
  "/administrations/facility/tat": [
    UserRoleEnums.ADMIN,
    // UserRoleEnums.SUPER_ADMIN,
  ],
  "/administrations/facility/testMeasures": [
    UserRoleEnums.ADMIN,
    // UserRoleEnums.SUPER_ADMIN,
  ],
  "/administrations/facility/machineSetting": [
    UserRoleEnums.ADMIN,
    // UserRoleEnums.SUPER_ADMIN,
  ],
  "/administrations/facility/manage-qc-test-config": [
    UserRoleEnums.ADMIN,
    // UserRoleEnums.SUPER_ADMIN,
  ],

  // Commodities (Admin + SuperAdmin)
  "/commodities/createAsset": [UserRoleEnums.ADMIN, UserRoleEnums.SUPER_ADMIN],
  "/commodities/asset": [UserRoleEnums.ADMIN, UserRoleEnums.SUPER_ADMIN],
  "/commodities/bulkCheckOutAsset": [
    UserRoleEnums.ADMIN,
    // UserRoleEnums.SUPER_ADMIN,
  ],
  "/commodities/createConsumable": [
    UserRoleEnums.ADMIN,
    // UserRoleEnums.SUPER_ADMIN,
  ],
  "/commodities/consumable": [UserRoleEnums.ADMIN, UserRoleEnums.SUPER_ADMIN],

  // GENERAL Program - Analytics
  "/clinical/dashboard": [UserRoleEnums.SUPERVISOR, UserRoleEnums.DATA_ANALYST],
  "/clinical/report": [UserRoleEnums.SUPERVISOR, UserRoleEnums.DATA_ANALYST],
  "/manage-result": [UserRoleEnums.SUPERVISOR, UserRoleEnums.DATA_ANALYST],

  // GENERAL Program - Main Operations
  "/clinical/complete-entry": [
    UserRoleEnums.SUPERVISOR,
    UserRoleEnums.DATA_CLERK,
    UserRoleEnums.RECEPTIONIST,
  ],
  "/clinical/lab-order": [
    UserRoleEnums.SUPERVISOR,
    UserRoleEnums.DATA_CLERK,
    UserRoleEnums.RECEPTIONIST,
  ],
  "/clinical/manage-sample": [
    UserRoleEnums.SUPERVISOR,
    UserRoleEnums.DATA_CLERK,
    UserRoleEnums.RECEPTIONIST,
  ],
  "/clinical/sample-reception": [
    UserRoleEnums.SUPERVISOR,
    UserRoleEnums.RECEPTIONIST,
  ],
  "/clinical/workplan": [UserRoleEnums.SUPERVISOR, UserRoleEnums.ANALYST],
  "/clinical/enter-results": [
    UserRoleEnums.SUPERVISOR,
    UserRoleEnums.ANALYST,
    UserRoleEnums.DATA_CLERK,
  ],
  "/clinical/validate-results": [UserRoleEnums.SUPERVISOR],
  "/clinical/approve-results": [UserRoleEnums.SUPERVISOR],

  // Universal Access (all authenticated users)
  "/file-management/documents": ["*"],
  "/file-management/recent-folders": ["*"],
  "/file-management/recent-files": ["*"],
  "/file-management/upload-files": ["*"],
  "/non-conformity": ["*"],
  "/user-profile/:name": ["*"],
};

// ============================================================
// MENU CONFIGURATION
// ============================================================

/**
 * Menu structure with role requirements
 * This powers the sidebar navigation
 */
export const MENU_CONFIG = {
  // Global Administration Menu (SuperAdmin only)
  globalAdministration: {
    key: "admin",
    title: "sidebar.admin",
    roles: [UserRoleEnums.SUPER_ADMIN],
    items: [
      {
        key: "administrations",
        title: "sidebar.globalAdministration",
        icon: "GlobalOutlined",
        submenu: [
          {
            key: "administrations/global/dashboard",
            path: "/administrations/global/dashboard",
            title: "sidebar.GlobalAdminDashboard",
          },
          {
            key: "administrations/global/manageFacility",
            path: "/administrations/global/manageFacility",
            title: "sidebar.manageFacility",
          },
          {
            key: "administrations/global/tierUsers",
            path: "/administrations/global/tierUsers",
            title: "sidebar.manageTierUsers",
          },
          {
            key: "administrations/global/testResultConfiguration",
            path: "/administrations/global/testResultConfiguration",
            title: "sidebar.testResultConfiguration",
          },
          {
            key: "administrations/global/supportRequests",
            path: "/administrations/global/support-requests",
            title: "sidebar.supportRequests",
          },
        ],
      },
    ],
  },

  // Facility Administration Menu (Admin only)
  facilityAdministration: {
    key: "facility_admin",
    title: "sidebar.facilityAdmin",
    roles: [UserRoleEnums.ADMIN],
    items: [
      {
        key: "facility-administrations",
        title: "sidebar.facilityAdministration",
        icon: "BankOutlined",
        submenu: [
          {
            key: "administrations/facility/facilityUsers",
            path: "/administrations/facility/facilityUsers",
            title: "sidebar.manageFacilityUser",
          },
          {
            key: "administrations/facility/testService",
            path: "/administrations/facility/testService",
            title: "sidebar.manageTestType",
          },
          {
            key: "administrations/facility/tat",
            path: "/administrations/facility/tat",
            title: "sidebar.manageTAT",
          },
          {
            key: "administrations/facility/testMeasures",
            path: "/administrations/facility/testMeasures",
            title: "sidebar.manageFacilityTestMeasure",
          },
        ],
      },
      {
        key: "commodities",
        title: "sidebar.facilityInventory",
        icon: "ScheduleOutlined",
        submenu: [
          {
            key: "commodities/createAsset",
            path: "/commodities/createAsset",
            title: "sidebar.createAsset",
          },
          {
            key: "commodities/asset",
            path: "/commodities/asset",
            title: "sidebar.manageAsset",
          },
          {
            key: "commodities/bulkCheckOutAsset",
            path: "/commodities/bulkCheckOutAsset",
            title: "sidebar.bulkCheckoutAsset",
          },
        ],
      },
      {
        key: "commodities_consumables",
        title: "sidebar.facilityConsumables",
        icon: "ScheduleOutlined",
        submenu: [
          {
            key: "commodities/createConsumable",
            path: "/commodities/createConsumable",
            title: "sidebar.createConsumables",
          },
          {
            key: "commodities/consumable",
            path: "/commodities/consumable",
            title: "sidebar.manageConsumables",
          },
        ],
      },
    ],
  },

  // File Management (Universal - all users)
  fileManagement: {
    key: "File Management",
    title: "sidebar.documents.fileManagement",
    roles: ["*"], // All users
    items: [
      {
        key: "file-management/documents",
        path: "/file-management/documents",
        title: "sidebar.documents",
        icon: "FileWordOutlined",
      },
      {
        key: "file-management/recent-folders",
        path: "/file-management/recent-folders",
        title: "Recent Folder",
        icon: "FolderViewOutlined",
      },
      {
        key: "file-management/recent-files",
        path: "/file-management/recent-files",
        title: "Recent files",
        icon: "FileSearchOutlined",
      },
      {
        key: "file-management/upload-files",
        path: "/file-management/upload-files",
        title: "sidebar.interoperability.upload",
        icon: "FileSearchOutlined",
      },
    ],
  },

  // Non Conformity (Universal - all users)
  nonConformity: {
    key: "Non Conformities",
    title: "sidebar.non_conformities",
    roles: ["*"], // All users
    items: [
      {
        key: "non-conformity",
        path: "/non-conformity",
        title: "Non Conformity",
        icon: "FileProtectOutlined",
      },
    ],
  },
  // In MENU_CONFIG, add:
  monitoring: {
    key: "monitoring",
    title: "sidebar.monitoring",
    roles: [
      UserRoleEnums.SUPER_ADMIN,
      UserRoleEnums.ADMIN,
      UserRoleEnums.SUPERVISOR,
      UserRoleEnums.DATA_ANALYST,
      UserRoleEnums.TIER_USER,
    ],
    items: [
      {
        key: "monitoring",
        path: "/monitoring",
        title: "National Lab Surveillance",
        icon: "FundProjectionScreenOutlined",
      },
    ],
  },
};

/**
 * Program-specific menu configuration
 * Used for facility users who have selected a program
 */
export const PROGRAM_MENU_CONFIG = {
  // GENERAL Program menus defined here...
  // TB Program menus defined here...
  // EID Program menus defined here...
  // etc.
  // (To be expanded based on existing SidebarContent.jsx)
};

// ============================================================
// FACILITY TYPE RESTRICTIONS
// ============================================================

/**
 * Some features are only available for specific facility types
 */
export const FACILITY_TYPE_RESTRICTIONS = {
  "/administrations/facility/machineSetting": ["public", "private"],
  "/administrations/facility/manage-qc-test-config": ["public", "private"],
};

// ============================================================
// EXPORT ALL
// ============================================================

export default {
  USER_TYPES,
  PROGRAM_INDEPENDENT_ROLES,
  FACILITY_ROLES,
  DEFAULT_ROUTES,
  ROUTE_PERMISSIONS,
  MENU_CONFIG,
  PROGRAM_MENU_CONFIG,
  FACILITY_TYPE_RESTRICTIONS,
};
